import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react"

import { Badge, type BadgeProps } from "@coval/ui/badge"
import { cn } from "@coval/ui/cn"

import type { InvestigationFinding } from "../utils/investigationFindings"

// The category chip carries the finding's severity, on the same scale the
// briefing ranks by: red (danger) for the categories that mean the agent failed
// or did something it should not have, orange (warning) for the ones that mean
// it behaved wrongly or was set up wrongly, blue (info) for an evaluator result
// that needs a human look, green (success) for the ones that only propose a
// better test or metric. A category this build has no entry for stays outlined
// rather than being assigned a severity by guesswork.
const CATEGORY_VARIANTS: Record<string, BadgeProps["variant"]> = {
  config_error: "warning",
  failure_pattern: "danger",
  metric_disagreement: "info",
  metric_improvement: "success",
  policy_safety: "danger",
  test_case_improvement: "success",
  unexpected_behavior: "warning",
}

interface FindingCardProps extends ComponentPropsWithoutRef<"li"> {
  finding: InvestigationFinding
  // The muted line that sits beside the badges: each surface names the timing
  // detail it cares about.
  meta?: ReactNode
  // Home's briefing sits under an h2, the history page under its own h1, so the
  // caller names the level that keeps its page's heading order intact.
  titleAs?: "h2" | "h3"
  // The history page lists findings in every status and has to label which one
  // this is; the briefing only ever shows actionable findings.
  showStatus?: boolean
}

// One finding, carded. Shared by Home's daily briefing and the investigation
// history page so a finding reads the same wherever it appears; each surface
// supplies its own controls and expandable panels as children.
export const FindingCard = forwardRef<HTMLLIElement, FindingCardProps>(
  function FindingCard(
    {
      children,
      className,
      finding,
      meta,
      showStatus = false,
      titleAs = "h3",
      ...rest
    },
    ref,
  ) {
    const Title = titleAs

    return (
      <li
        ref={ref}
        className={cn(
          "space-y-3 rounded-md border border-coval-border bg-surface-default p-4",
          className,
        )}
        data-covi-highlight-id={finding.findingId}
        data-covi-name={finding.title}
        data-covi-status={finding.status}
        {...rest}
      >
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={CATEGORY_VARIANTS[finding.category] ?? "outline"}>
              {finding.categoryLabel}
            </Badge>
            {showStatus && (
              <Badge
                variant={finding.status === "resolved" ? "contrast" : "outline"}
              >
                {finding.status}
              </Badge>
            )}
            {finding.isRegression && <Badge variant="danger">Regression</Badge>}
            {meta ? (
              <p className="text-xs text-coval-text-muted">{meta}</p>
            ) : null}
          </div>
          <Title className="font-medium leading-5 text-coval-text">
            {finding.title}
          </Title>
          <p className="text-sm leading-5 text-coval-text">
            {finding.narrative}
          </p>
        </div>
        {children}
      </li>
    )
  },
)
